import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import { cn } from "@/lib/utils";

const ROW_H_PX = 22;
const STICKY_Z_BASE = 20;

type JsonTreeProps = {
  value: unknown;
  className?: string;
  defaultExpandDepth?: number;
};

type ScrollApi = {
  rootRef: RefObject<HTMLDivElement | null>;
  scrollToBlock: (anchor: HTMLElement, depth: number) => void;
};

const JsonTreeScrollContext = createContext<ScrollApi | null>(null);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function collapsedPreview(value: unknown): string {
  if (Array.isArray(value)) {
    const n = value.length;
    return n === 0 ? "[]" : `[${n}]`;
  }
  if (isPlainObject(value)) {
    const n = Object.keys(value).length;
    return n === 0 ? "{}" : `{${n}}`;
  }
  return "";
}

function offsetWithin(el: HTMLElement, container: HTMLElement): number {
  return (
    el.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop
  );
}

function JsonPrimitive({ value }: { value: unknown }) {
  if (value === null) {
    return <span className="text-muted-foreground">null</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span className="text-violet-700 dark:text-violet-300">
        {value ? "true" : "false"}
      </span>
    );
  }
  if (typeof value === "number") {
    return (
      <span className="text-amber-700 dark:text-amber-300">
        {Number.isFinite(value) ? String(value) : "null"}
      </span>
    );
  }
  if (typeof value === "string") {
    return (
      <span className="text-emerald-700 dark:text-emerald-300">
        {JSON.stringify(value)}
      </span>
    );
  }
  return (
    <span className="text-muted-foreground">{JSON.stringify(value)}</span>
  );
}

function KeyLabel({
  name,
  index,
}: {
  name?: string;
  index?: number;
}) {
  if (name !== undefined) {
    return (
      <>
        <span className="text-sky-700 dark:text-sky-300">
          {JSON.stringify(name)}
        </span>
        <span className="text-muted-foreground">: </span>
      </>
    );
  }
  if (index !== undefined) {
    return (
      <>
        <span className="text-sky-700/80 dark:text-sky-300/80">{index}</span>
        <span className="text-muted-foreground">: </span>
      </>
    );
  }
  return null;
}

function JsonNode({
  name,
  index,
  value,
  depth,
  defaultExpandDepth,
  isLast,
}: {
  name?: string;
  index?: number;
  value: unknown;
  depth: number;
  defaultExpandDepth: number;
  isLast: boolean;
}) {
  const scroll = useContext(JsonTreeScrollContext);
  const blockRef = useRef<HTMLDivElement>(null);
  const expandable =
    (Array.isArray(value) && value.length > 0) ||
    (isPlainObject(value) && Object.keys(value).length > 0);
  const emptyContainer =
    (Array.isArray(value) && value.length === 0) ||
    (isPlainObject(value) && Object.keys(value).length === 0);
  const [open, setOpen] = useState(expandable && depth < defaultExpandDepth);

  const jumpToBlock = useCallback(() => {
    const anchor = blockRef.current;
    if (!anchor || !scroll) return;
    scroll.scrollToBlock(anchor, depth);
  }, [depth, scroll]);

  if (!expandable && !emptyContainer) {
    return (
      <div className="min-h-[var(--json-tree-row-h)] leading-[var(--json-tree-row-h)]">
        <KeyLabel name={name} index={index} />
        <JsonPrimitive value={value} />
        {isLast ? null : <span className="text-muted-foreground">,</span>}
      </div>
    );
  }

  const openBracket = Array.isArray(value) ? "[" : "{";
  const closeBracket = Array.isArray(value) ? "]" : "}";
  const entries = Array.isArray(value)
    ? value.map((item, i) => ({
        key: String(i),
        index: i,
        name: undefined as string | undefined,
        item,
      }))
    : Object.entries(value as Record<string, unknown>).map(([key, item]) => ({
        key,
        index: undefined as number | undefined,
        name: key,
        item,
      }));

  if (emptyContainer) {
    return (
      <div className="min-h-[var(--json-tree-row-h)] leading-[var(--json-tree-row-h)]">
        <KeyLabel name={name} index={index} />
        <span className="text-foreground">{openBracket}</span>
        <span className="text-foreground">{closeBracket}</span>
        {isLast ? null : <span className="text-muted-foreground">,</span>}
      </div>
    );
  }

  return (
    <div ref={blockRef} className="relative">
      <div
        className="sticky flex min-h-[var(--json-tree-row-h)] items-start gap-0.5 border-b border-border/40 bg-[color-mix(in_oklch,var(--muted)_88%,var(--background))] leading-[var(--json-tree-row-h)] backdrop-blur-sm"
        style={
          {
            top: depth * ROW_H_PX,
            zIndex: STICKY_Z_BASE - depth,
          } satisfies CSSProperties
        }
      >
        <button
          type="button"
          className="mt-px inline-flex size-[var(--json-tree-row-h)] shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
          aria-expanded={open}
          aria-label={open ? "折叠" : "展开"}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          <span className="select-none" aria-hidden>
            {open ? "▾" : "▸"}
          </span>
        </button>
        <button
          type="button"
          className="min-w-0 flex-1 rounded-sm px-0.5 text-left break-all hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
          title="滚动到此节点"
          onClick={jumpToBlock}
        >
          <KeyLabel name={name} index={index} />
          <span className="text-foreground">{openBracket}</span>
          {!open ? (
            <>
              <span className="text-muted-foreground">
                {" "}
                {collapsedPreview(value)}{" "}
              </span>
              <span className="text-foreground">{closeBracket}</span>
              {isLast ? null : (
                <span className="text-muted-foreground">,</span>
              )}
            </>
          ) : null}
        </button>
      </div>
      {open ? (
        <>
          <div className="ml-3 border-l border-border/70 pl-3">
            {entries.map((entry, entryIndex) => (
              <JsonNode
                key={entry.key}
                name={entry.name}
                index={entry.index}
                value={entry.item}
                depth={depth + 1}
                defaultExpandDepth={defaultExpandDepth}
                isLast={entryIndex === entries.length - 1}
              />
            ))}
          </div>
          <div className="min-h-[var(--json-tree-row-h)] leading-[var(--json-tree-row-h)]">
            <span className="text-foreground">{closeBracket}</span>
            {isLast ? null : <span className="text-muted-foreground">,</span>}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function JsonTree({
  value,
  className,
  defaultExpandDepth = Number.POSITIVE_INFINITY,
}: JsonTreeProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  const scrollToBlock = useCallback((anchor: HTMLElement, depth: number) => {
    const root = rootRef.current;
    if (!root) return;
    const top = offsetWithin(anchor, root) - depth * ROW_H_PX;
    root.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, []);

  return (
    <JsonTreeScrollContext.Provider value={{ rootRef, scrollToBlock }}>
      <div
        ref={rootRef}
        className={cn(
          "overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[0.7rem]",
          className,
        )}
        style={
          {
            "--json-tree-row-h": `${ROW_H_PX}px`,
          } as CSSProperties
        }
      >
        <JsonNode
          value={value}
          depth={0}
          defaultExpandDepth={defaultExpandDepth}
          isLast
        />
      </div>
    </JsonTreeScrollContext.Provider>
  );
}

export function parseJsonText(text: string):
  | { ok: true; value: unknown }
  | { ok: false; error: string } {
  const trimmed = text.trim();
  if (!trimmed) {
    return { ok: false, error: "empty" };
  }
  try {
    return { ok: true, value: JSON.parse(trimmed) as unknown };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "invalid_json",
    };
  }
}
